/**
 * my_drive Picker 模式（popup）— Task-31 PR 5 階段 C2
 *
 * 給 Roundcube webmail（或其他外部嵌入）compose 時開 popup 選 my_drive 檔案用。
 * 流程：
 *   webmail plugin JS:  window.open('https://<base-ap>/ee_my_drive_picker', 'mydrive', ...)
 *   本頁（登入後）:      全畫面 FilePicker → 選檔 → postMessage 回 opener → 自動關窗
 *   webmail plugin JS:  接 message（嚴格白名單 origin）→ 用 WebDAV GET 檔 → attach 進 compose
 *
 * 安全：
 *   - 路由在 PrivateRoute 內（需登入 base AP），但在 MainLayout 外（無 SideNav/Toolbar）
 *   - postMessage 的 targetOrigin 取自 opener（document.referrer）且必須命中白名單，否則拒送
 *   - 白名單由 REACT_APP_PICKER_ALLOWED_ORIGINS（逗號分隔）設定；未設定時退回 same-origin
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Alert, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import FilePicker from '../../../components/FilePicker/FilePicker';
import myDriveService, { DriveFile } from './services/myDriveService';

/** postMessage 訊息型別（與 webmail plugin 端契約）。 */
export interface MyDrivePickedFile {
  fileId: number;
  filename: string;
  category: string;
  relativePath: string;
  mimeType: string;
  /** WebDAV 相對路徑：plugin 端用 WebDAV base URL 前綴後 GET。 */
  webdavPath: string;
}
export interface MyDrivePickMessage {
  type: 'mydrive-pick';
  files: MyDrivePickedFile[];
}
export interface MyDriveCancelMessage {
  type: 'mydrive-cancel';
}

/** 解析允許回傳的 origin 白名單。 */
function allowedOrigins(): string[] {
  const env = process.env.REACT_APP_PICKER_ALLOWED_ORIGINS;
  const list = (env ? env.split(',') : [])
    .map(s => s.trim())
    .filter(Boolean);
  // 未設定白名單 → 退回 same-origin（dev / 同源嵌入仍可用）
  if (list.length === 0) list.push(window.location.origin);
  return list;
}

/** 取 opener 的 origin（來自 document.referrer），命中白名單才回傳，否則 null。 */
function resolveTargetOrigin(): string | null {
  const allow = allowedOrigins();
  // 1. 優先讀 URL param ?from=（plugin 開 popup 時主動帶，比 referrer 可靠）。
  //    param 可被偽造，故仍須命中白名單才信 —— 安全完全由白名單把關。
  try {
    const fromParam = new URLSearchParams(window.location.search).get('from');
    if (fromParam && allow.includes(fromParam)) return fromParam;
  } catch { /* silent ok: URL 解析失敗走後續分支 */ }

  // 2. fallback：document.referrer（同源 / browser 有保留時）
  let candidate = '';
  try {
    if (document.referrer) candidate = new URL(document.referrer).origin;
  } catch { /* silent ok: referrer 解析失敗時走 null 分支顯示錯誤 */ }
  if (candidate && allow.includes(candidate)) return candidate;
  // 同源 opener（referrer 空但確實是同源開的）→ 允許 same-origin
  if (allow.includes(window.location.origin) && (!candidate || candidate === window.location.origin)) {
    return window.location.origin;
  }
  return null;
}

const MyDrivePickerPage: React.FC = () => {
  const { t } = useTranslation();
  const targetOrigin = useMemo(resolveTargetOrigin, []);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 沒有合法 opener origin → 不可送，顯示錯誤（避免把檔案資訊 postMessage 到非白名單站台）
  useEffect(() => {
    if (!targetOrigin) {
      setError(t('myDrivePicker.badOrigin',
        '無法確認來源視窗（origin 不在白名單），為安全起見停用選取。'));
    }
  }, [targetOrigin, t]);

  const postAndClose = (msg: MyDrivePickMessage | MyDriveCancelMessage) => {
    if (targetOrigin && window.opener) {
      window.opener.postMessage(msg, targetOrigin);
    }
    window.close();
  };

  const handlePick = async (fileIds: number[]) => {
    if (!targetOrigin) return;
    setSending(true);
    try {
      const metas: DriveFile[] = await myDriveService.getFilesMeta(fileIds);
      const byId = new Map(metas.map(m => [m.id, m]));
      const files: MyDrivePickedFile[] = fileIds.map(id => {
        const m = byId.get(id);
        const category = m?.dir_category ?? '';
        const relativePath = m?.relative_path ?? '';
        return {
          fileId: id,
          filename: m?.filename ?? `#${id}`,
          category,
          relativePath,
          mimeType: m?.mime_type ?? 'application/octet-stream',
          webdavPath: category && relativePath ? `${category}/${relativePath}` : '',
        };
      });
      postAndClose({ type: 'mydrive-pick', files });
    } catch {
      setError(t('myDrivePicker.metaFailed', '取得檔案資訊失敗，請重試。'));
      setSending(false);
    }
  };

  const handleCancel = () => postAndClose({ type: 'mydrive-cancel' });

  if (error) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', p: 3 }}>
        <Alert severity="warning" sx={{ maxWidth: 420 }}>{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle1">
          {t('myDrivePicker.title', '從我的網路硬碟選取附件')}
        </Typography>
      </Box>
      {sending && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">
            {t('myDrivePicker.sending', '正在回傳所選檔案…')}
          </Typography>
        </Box>
      )}
      {/* FilePicker 永遠 open；onClose = 取消並關窗 */}
      <FilePicker
        open
        multiSelect
        onClose={handleCancel}
        onPick={handlePick}
        title={t('myDrivePicker.title', '從我的網路硬碟選取附件')}
      />
    </Box>
  );
};

export default MyDrivePickerPage;
